// frontend/src/components/Samples/LineOperationDetailsModal.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiX,
  FiSave,
  FiPaperclip,
  FiClock,
  FiUser,
} from 'react-icons/fi';
import { useTranslation } from '../../context/TranslationContext';
import SearchableSelect from '../Common/SearchableSelect';
import FileUpload from '../Common/FileUpload';
import CommentsButton from '../Common/Comments/CommentsButton';
import AuditLogPanel from '../Common/AuditLog/AuditLogPanel';
import './LineOperationDetailsModal.css';
import { TASK_STATUS_META, statusPillStyle } from '../../constants/statusMeta';


const parseOptions = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw.length ? raw : null;

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch {
    return null;
  }
};

const getOperationUI = (op, t) => {
  const options = parseOptions(op.field_options || op.bool_field_label);

  if (options?.length) {
    return {
      kind: 'choice',
      label: op.bool_field_label || t('areas.result', 'Resultado'),
      options,
    };
  }

  if (op.text_field_label) {
    return {
      kind: 'text',
      label: op.text_field_label || t('areas.result', 'Resultado'),
    };
  }

  return {
    kind: 'notes_only',
    label: t('areas.observation', 'Observação'),
  };
};

const PasteTextarea = ({ value, onChange, onImagePaste, placeholder, rows = 2 }) => {
  const ref = useRef(null);

  const handlePaste = useCallback((e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imgItem = items.find((i) => i.type.startsWith('image/'));
    if (!imgItem) return;

    e.preventDefault();
    const file = imgItem.getAsFile();
    if (file && onImagePaste) onImagePaste(file);
  }, [onImagePaste]);

  return (
    <textarea
      ref={ref}
      className="field-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onPaste={handlePaste}
      placeholder={placeholder}
      rows={rows}
    />
  );
};

const ImageThumbs = ({ files, onRemove }) => {
  if (!files?.length) return null;

  return (
    <div className="pending-images">
      {files.map((file, i) => (
        <div key={i} className="pending-img-wrap">
          <img src={URL.createObjectURL(file)} alt="" className="pending-img" />
          <button
            type="button"
            className="pending-img-remove"
            onClick={() => onRemove(i)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

const LineOperationDetailsModal = ({
  op,
  form,
  assignableUsers,
  lineArticle,   // { enteredProductCode, enteredProductColor, erpProductCode, erpProductColorCode }
  lineLabel,     // fallback subtitle from LineOperationsModal (e.g. "69140 T-Shirt")
  onClose,
  onSave,
  orderId,          // sample_order_header.sample_order_id — comments are stored on the order
  commentLabel,     // task reference saved with each comment (built by the parent)
  onCommentsChange, // optional (lineOperationsId, {total, unread}) — keeps parent row badge in sync
}) => {
  const { t } = useTranslation();
  const [localForm, setLocalForm] = useState(form);
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const ui = useMemo(() => getOperationUI(op, t), [op, t]);

  // 🛠 Debug — shows which modal is mounted and its key data in the browser console.
  // Remove before going to production.
  const todayStr = () => new Date().toISOString().slice(0, 10);

  const set = (key, value) => {
    setLocalForm((prev) => {
      const next = { ...prev, [key]: value };
      // Status <-> real_end_date auto-rules
      if (key === 'status') {
        if (value === 'COMPLETED' && !next.real_end_date) {
          next.real_end_date = todayStr();
        } else if (value !== 'COMPLETED' && next.real_end_date) {
          next.real_end_date = '';
        }
      }
      return next;
    });
  };

  const addImage = (file) => {
    setLocalForm((prev) => ({
      ...prev,
      pendingImages: [...(prev.pendingImages || []), file],
    }));
  };

  const removeImg = (index) => {
    setLocalForm((prev) => ({
      ...prev,
      pendingImages: (prev.pendingImages || []).filter((_, i) => i !== index),
    }));
  };

  const handleSave = () => {
    // Pass the full localForm (including pendingImages) to parent.
    // The parent (handleDetailsSave) handles upload after it knows the line_operations_id.
    onSave(localForm);
  };

  return (
    <div className="odm-overlay" onClick={onClose}>
      <div className="odm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="odm-header">
          <div className="odm-header-left">
            <h3 className="odm-title">{op.description || op.operation_label}</h3>

            <div className="odm-subline">
              <span className="odm-area-badge">{op.area_code}</span>

              {/* Product info — show article fields or fall back to lineLabel */}
              {(() => {
                const code  = lineArticle?.enteredProductCode || lineArticle?.erpProductCode || lineLabel;
                const color = lineArticle?.enteredProductColor;
                const erp   = lineArticle?.erpProductCode;
                const erpColor = lineArticle?.erpProductColorCode;
                const erpDiffers = erp && erp !== lineArticle?.enteredProductCode;
                return (
                  <>
                    {code && (
                      <span className="odm-meta-pill odm-meta-product">
                        {code}
                        {color && <span style={{ marginLeft: 4, fontWeight: 400, color: 'var(--gray-400)' }}>· {color}</span>}
                      </span>
                    )}
                    {erpDiffers && (
                      <span className="odm-meta-pill odm-meta-erp">
                        {erp}{erpColor ? ` / ${erpColor}` : ''}
                      </span>
                    )}
                  </>
                );
              })()}

              {op.assigned_user_name && (
                <span className="odm-meta-pill">
                  <FiUser size={12} /> {op.assigned_user_name}
                </span>
              )}

              {op.planned_end_date && (
                <span className="odm-meta-pill">
                  <FiClock size={12} /> {new Date(op.planned_end_date).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {/* Comments — available once the task exists in the DB */}
            <CommentsButton
              entityType="sample_order_header"
              entityId={orderId}
              subEntityType="line_operation"
              subEntityId={localForm.line_operations_id}
              subEntityLabel={commentLabel}
              onCountsChange={onCommentsChange}
              disabledTitle="Guarde os detalhes primeiro para poder comentar"
            />
            {localForm.line_operations_id && (
              <button
                type="button"
                className="comments-btn"
                title="Ver histórico de alterações"
                onClick={() => setShowAuditLog(true)}
              >
                <FiClock size={16} />
              </button>
            )}
            <button type="button" className="odm-close" onClick={onClose}>
              <FiX size={18} />
            </button>
          </div>
        </div>

        <div className="odm-body">
          <div className="field-grid-2">
            <div className="field-row field-span-all">
              <label className="field-label">Responsável</label>
              <SearchableSelect
                value={localForm.assigned_user_id || ''}
                onChange={(val) => set('assigned_user_id', val ? String(val) : '')}
                options={assignableUsers || []}
                getOptionLabel={(u) => u.full_name || u.username}
                getOptionValue={(u) => String(u.user_id)}
                placeholder="— sem responsável —"
              />
            </div>

            <div className="field-row field-span-all">
              <label className="field-label">Estado</label>
              <div className="status-pill-group">
                {Object.entries(TASK_STATUS_META).map(([k, m]) => {
                  const active = (localForm.status || 'NEW') === k;
                  return (
                    <button key={k} type="button"
                      onClick={() => set('status', k)}
                      className="status-pill"
                      style={{ border: '1.5px solid', ...statusPillStyle(m, active) }}>
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="field-row">
              <label className="field-label">Início planeado</label>
              <input
                type="date"
                className="field-input"
                value={localForm.planned_start_date || ''}
                {...(!localForm.planned_start_date ? { 'data-empty': true } : {})}
                onChange={(e) => set('planned_start_date', e.target.value)}
              />
            </div>

            <div className="field-row">
              <label className="field-label">Fim planeado</label>
              <input
                type="date"
                className="field-input"
                value={localForm.planned_end_date || ''}
                {...(!localForm.planned_end_date ? { 'data-empty': true } : {})}
                onChange={(e) => set('planned_end_date', e.target.value)}
              />
            </div>

            <div className="field-row">
              <label className="field-label">Início real</label>
              <input
                type="date"
                className="field-input"
                value={localForm.real_start_date || ''}
                {...(!localForm.real_start_date ? { 'data-empty': true } : {})}
                onChange={(e) => set('real_start_date', e.target.value)}
              />
            </div>

            <div className="field-row">
              <label className="field-label">Fim real</label>
              <input
                type="date"
                className="field-input"
                value={localForm.real_end_date || ''}
                {...(!localForm.real_end_date ? { 'data-empty': true } : {})}
                onChange={(e) => set('real_end_date', e.target.value)}
              />
            </div>
          </div>

          {ui.kind === 'choice' && (
            <div className="field-row">
              <label className="field-label">
                {ui.label} <span className="field-hint">(obrigatório)</span>
              </label>

              <div className="pill-group">
                {ui.options.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`pill ${localForm.choice_value === opt ? 'selected' : ''}`}
                    onClick={() => set('choice_value', localForm.choice_value === opt ? '' : opt)}
                  >
                    {opt}
                  </button>
                ))}

                {localForm.choice_value && (
                  <button
                    type="button"
                    className="pill-clear"
                    onClick={() => set('choice_value', '')}
                  >
                    limpar
                  </button>
                )}
              </div>
            </div>
          )}

          {ui.kind === 'text' && (
            <div className="field-row">
              <label className="field-label">
                {ui.label} <span className="field-hint">(obrigatório)</span>
              </label>

              <PasteTextarea
                value={localForm.text_value || ''}
                onChange={(v) => set('text_value', v)}
                onImagePaste={addImage}
                placeholder={`${ui.label}… (pode colar imagens)`}
                rows={2}
              />
            </div>
          )}

          <div className="field-row">
            <label className="field-label">
              {t('areas.operation_instructions', 'Instruções da Operação')} <span className="field-hint">(opcional)</span>
            </label>

            <PasteTextarea
              value={localForm.operation_instructions || ''}
              onChange={(v) => set('operation_instructions', v)}
              onImagePaste={addImage}
              placeholder={t('areas.operation_instructions_placeholder', 'Instruções da operação… (pode colar imagens)')}
              rows={3}
            />
          </div>

          <div className="field-row">
            <label className="field-label">
              {t('areas.completion_notes', 'Notas de Conclusão')} <span className="field-hint">(opcional)</span>
            </label>

            <PasteTextarea
              value={localForm.completion_notes || ''}
              onChange={(v) => set('completion_notes', v)}
              onImagePaste={addImage}
              placeholder={t('areas.completion_notes_placeholder', 'Notas de conclusão… (pode colar imagens)')}
              rows={3}
            />
          </div>

          <div className="field-row">
            <label className="field-label">
              <FiPaperclip size={12} /> Anexos <span className="field-hint">(opcional)</span>
            </label>

            {localForm.line_operations_id ? (
              // Operation exists in DB — show full FileUpload with existing files + pending
              <FileUpload
                key={fileUploadKey}
                entityType="line_operation"
                entityId={localForm.line_operations_id}
                compact={true}
                inline={false}
                label=""
                pendingFiles={localForm.pendingImages || []}
                onPendingFilesChange={(imgs) =>
                  setLocalForm((prev) => ({ ...prev, pendingImages: imgs }))
                }
              />
            ) : (
              // Not yet saved — collect pending files; they'll upload when Guardar is clicked
              <>
                <ImageThumbs files={localForm.pendingImages || []} onRemove={removeImg} />
                <label className="file-drop">
                  <FiPaperclip size={14} />
                  Anexar ficheiros (guardados ao clicar em Guardar detalhes)
                  <input type="file" multiple style={{ display: 'none' }}
                    onChange={e => {
                      const files = Array.from(e.target.files || []);
                      files.forEach(addImage);
                      e.target.value = '';
                    }}
                  />
                </label>
              </>
            )}
          </div>


        </div>

        <div className="odm-footer">
          <button type="button" className="btn-secondary btn-sm" onClick={onClose}>
            {t('cancel', 'Cancelar')}
          </button>
          <button type="button" className="btn-primary btn-sm" onClick={handleSave}>
            <FiSave size={13} /> Guardar detalhes
          </button>
        </div>
      </div>

      {localForm.line_operations_id && showAuditLog && (
        // Wrapper stops clicks inside the panel from reaching .odm-overlay (which closes this modal)
        <div onClick={(e) => e.stopPropagation()}>
          <AuditLogPanel
            isOpen={showAuditLog}
            onClose={() => setShowAuditLog(false)}
            entityType="line_operation"
            entityId={localForm.line_operations_id?.toString()}
            title={`${t('audit.title', 'Histórico de Alterações')} - ${op.description || op.operation_label}`}
          />
        </div>
      )}
    </div>
  );
};

export default LineOperationDetailsModal;